import React, { useEffect, useState } from "react";
import { Truck, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Extra-charges editor (freight + installation).
 * Renders as `no-print` — only visible on-screen for admin/employee.
 *
 * Props:
 *  - endpoint: PATCH URL, e.g. `/quotations/${id}/charges` or `/mezzanine/quotations/${id}/charges`
 *  - freight:  current freight_amount (number)
 *  - installation: current installation_amount (number)
 *  - onUpdated: (updatedQuotation) => void
 */
export default function ChargesEditor({ endpoint, freight = 0, installation = 0, onUpdated }) {
  const [f, setF] = useState(String(freight ?? 0));
  const [i, setI] = useState(String(installation ?? 0));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setF(String(freight ?? 0));
    setI(String(installation ?? 0));
  }, [freight, installation]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(endpoint, {
        freight_amount: Math.max(0, parseFloat(f) || 0),
        installation_amount: Math.max(0, parseFloat(i) || 0),
      });
      onUpdated?.(data);
      toast.success("Charges updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 border-t border-line no-print" data-testid="charges-editor">
      <div className="label-tech mb-3">Extra charges</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        <div>
          <label className="text-[0.65rem] uppercase tracking-wider mono text-ink-muted mb-1.5 flex items-center gap-1.5">
            <Truck size={12} /> Freight (₹)
          </label>
          <input
            type="number"
            min="0"
            step="1"
            className="input-tech w-full"
            value={f}
            onChange={(e) => setF(e.target.value)}
            data-testid="freight-input"
          />
        </div>
        <div>
          <label className="text-[0.65rem] uppercase tracking-wider mono text-ink-muted mb-1.5 flex items-center gap-1.5">
            <Wrench size={12} /> Installation (₹)
          </label>
          <input
            type="number"
            min="0"
            step="1"
            className="input-tech w-full"
            value={i}
            onChange={(e) => setI(e.target.value)}
            data-testid="installation-input"
          />
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="save-charges-btn"
        >
          {saving ? "Saving…" : "Update Charges"}
        </button>
      </div>
      <div className="text-[0.7rem] text-ink-muted mt-2">
        Charges appear in the printed quotation & are added to the grand total. Set to 0 to hide.
      </div>
    </div>
  );
}
